import { useRef, useState, useEffect, useCallback } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Camera, Trash2, Pencil, Upload, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import Cropper from "react-easy-crop";
import { useProfilePictureGeneric } from "@/lib/hooks/use-profile-picture-generic";
import type { Area, Point } from "react-easy-crop";

type ProfileType = 'freelancer' | 'client';

interface ProfilePictureUploadGenericProps {
  profileType: ProfileType;
  profileId?: string | null;
  currentUrl?: string | null;
  name?: string | null;
  className?: string;
  onImageUploaded?: (imageUrl: string) => void;
  onImageRemoved?: () => void;
}

// Keep in sync with backend `ALLOWED_FILE_TYPES.profile-pictures` in
// `backend/src/services/storage.service.ts`
const ALLOWED_PROFILE_PICTURE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
] as const;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Helper function to create a cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  // Set canvas size to match the crop area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped portion of the image onto the canvas
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.9
    );
  });
};

export function ProfilePictureUploadGeneric({ 
  profileType, 
  profileId, 
  currentUrl, 
  name,
  className = "",
  onImageUploaded,
  onImageRemoved
}: ProfilePictureUploadGenericProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadProfilePicture, deleteProfilePicture, isUploading, isDeleting, uploadedImageUrl, isUploadSuccess, isDeleteSuccess } = useProfilePictureGeneric({
    profileType,
    profileId,
  });

  // State to track the displayed image URL for smoother transitions
  const [displayedImageUrl, setDisplayedImageUrl] = useState(currentUrl);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [wasDeleting, setWasDeleting] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const previousUrlRef = useRef<string | null | undefined>(currentUrl);

  // Update displayed image when current URL changes
  useEffect(() => {
    if (currentUrl !== displayedImageUrl) {
      // If it's a blob URL (optimistic), show it immediately
      if (currentUrl?.startsWith('blob:')) {
        setDisplayedImageUrl(currentUrl);
        setIsImageLoading(false);
        previousUrlRef.current = currentUrl;
      } else if (currentUrl) {
        // If it's a real URL, preload it first for smooth transition
        setIsImageLoading(true);
        const img = new Image();
        img.onload = () => {
          setDisplayedImageUrl(currentUrl);
          setIsImageLoading(false);
          previousUrlRef.current = currentUrl;
        };
        img.onerror = () => {
          setDisplayedImageUrl(currentUrl); // Show anyway if loading fails
          setIsImageLoading(false);
          previousUrlRef.current = currentUrl;
        };
        img.src = currentUrl;
      } else {
        // No image
        setDisplayedImageUrl(null);
        setIsImageLoading(false);
        previousUrlRef.current = currentUrl;
      }
    }
  }, [currentUrl, displayedImageUrl]);

  // Handle image loading when displayedImageUrl changes to a new non-blob URL (e.g., after upload)
  useEffect(() => {
    // Check if displayedImageUrl changed to a new non-blob URL
    if (
      displayedImageUrl && 
      !displayedImageUrl.startsWith('blob:') &&
      displayedImageUrl !== previousUrlRef.current
    ) {
      // Verify if the image is actually loaded
      const img = new Image();
      const urlToCheck = displayedImageUrl;
      
      // Set loading state initially
      setIsImageLoading(true);
      
      img.onload = () => {
        // Only update if this is still the current URL being checked
        if (displayedImageUrl === urlToCheck) {
          setIsImageLoading(false);
          previousUrlRef.current = urlToCheck;
        }
      };
      img.onerror = () => {
        // Only update if this is still the current URL being checked
        if (displayedImageUrl === urlToCheck) {
          setIsImageLoading(false);
          previousUrlRef.current = urlToCheck;
        }
      };
      
      // If the image is already cached and loaded, it might be complete immediately
      img.src = urlToCheck;
      if (img.complete && img.naturalWidth > 0 && displayedImageUrl === urlToCheck) {
        setIsImageLoading(false);
        previousUrlRef.current = urlToCheck;
      }
    } else if (!displayedImageUrl) {
      // No image to display
      setIsImageLoading(false);
      previousUrlRef.current = null;
    }
  }, [displayedImageUrl]);

  // Handle successful image upload
  useEffect(() => {
    if (isUploadSuccess && uploadedImageUrl) {
      // For onboarding, call the callback
      if (onImageUploaded && !profileId) {
        onImageUploaded(uploadedImageUrl);
      }
      // Set loading state and update displayed image
      // The image preloading useEffect will handle the actual loading state
      setIsImageLoading(true);
      setDisplayedImageUrl(uploadedImageUrl);
      // Close dialog only after successful upload
      setIsDialogOpen(false);
      // Reset cropping state
      setIsCropping(false);
    }
  }, [isUploadSuccess, uploadedImageUrl, onImageUploaded, profileId]);

  // Track deletion state to detect when it completes
  useEffect(() => {
    setWasDeleting(isDeleting);
  }, [isDeleting]);

  // Handle successful deletion - close dialogs when deletion completes
  useEffect(() => {
    if (wasDeleting && !isDeleting && showDeleteConfirm) {
      // Delete API call completed (was deleting, now not deleting)
      // Close confirmation dialog
      setShowDeleteConfirm(false);
      // Also close main dialog if it was open
      setIsDialogOpen(false);
      setWasDeleting(false);
    }
  }, [wasDeleting, isDeleting, showDeleteConfirm]);

  // Handle successful deletion - call callback for onboarding
  useEffect(() => {
    if (isDeleteSuccess && !profileId && onImageRemoved) {
      // For onboarding (no profileId), call the callback to update form data
      // The currentUrl prop will update, which will trigger the displayedImageUrl update
      onImageRemoved();
    }
  }, [isDeleteSuccess, profileId, onImageRemoved]);

  // Reset dialog state when closed
  useEffect(() => {
    if (!isDialogOpen) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsCropping(false);
      setIsDragging(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isDialogOpen]);

  const initials = (() => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/).filter((w) => w.length > 0);
    if (words.length === 0) return "?";
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }
    // 2 or more words: use first letter of first word and first letter of last word
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  })();

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Process a file and set it as the image source
  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Restrict to the same formats allowed by the backend storage service
    if (!ALLOWED_PROFILE_PICTURE_MIME_TYPES.includes(file.type as (typeof ALLOWED_PROFILE_PICTURE_MIME_TYPES)[number])) {
      toast.error('Invalid image format. Allowed formats: JPEG (.jpg, .jpeg), PNG, WEBP');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    // Create image preview URL
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    });
    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    processFile(file);
    
    // Clear the input to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading && !isDeleting && !isCropping) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading && !isDeleting && !isCropping) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isUploading || isDeleting || isCropping) {
      return;
    }

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleSaveCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast.error('Please select and crop an image first');
      return;
    }

    setIsCropping(true);
    try {
      // Step 1: Crop the image (client-side processing)
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedImageBlob], 'profile-picture.jpg', { type: 'image/jpeg' });
      
      // Step 2: Upload the cropped image (API call)
      // Note: isUploading will track this, and useEffect will close dialog on success
      uploadProfilePicture(croppedFile);
      // Don't close dialog or reset state here - wait for success via useEffect
    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error('Failed to crop image. Please try again.');
      setIsCropping(false);
    }
  };

  const handleDelete = () => {
    // Show confirmation dialog
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    // Use the current URL (not the displayed one which might be a blob)
    if (currentUrl && !currentUrl.startsWith('blob:')) {
      deleteProfilePicture(currentUrl);
      // Don't close dialogs here - wait for delete to complete via useEffect
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open && imageSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(imageSrc);
    }
  };

  return (
    <>
      <div className={`relative inline-block ${className}`}>
        <div className="group relative cursor-pointer" onClick={() => setIsDialogOpen(true)}>
          <Avatar className="h-20 w-20 transition-opacity group-hover:opacity-80">
            <AvatarImage 
              src={displayedImageUrl || undefined} 
              alt={name || "Profile"} 
            />
            <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
          </Avatar>
          {/* Loading overlay when image is loading after upload */}
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100/90 rounded-full">
              <Loader2 className="h-6 w-6 animate-spin text-[#4669ed]" />
            </div>
          )}
          {/* Pencil icon on top right corner */}
          <div className={`absolute -top-1 -right-1 bg-[#4669ed] rounded-full p-1.5 shadow-lg border-2 border-white z-10 ${isImageLoading ? 'opacity-50' : ''}`}>
            <Pencil className="h-3 w-3 text-white" />
          </div>
          {/* Edit overlay on hover */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-white rounded-full p-2 shadow-lg">
              <Camera className="h-4 w-4 text-slate-700" />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="!max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto rounded-[24px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-normal text-slate-900">
              {displayedImageUrl ? 'Edit Profile Picture' : 'Upload Profile Picture'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Upload and crop your profile picture.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Current Image Display (if exists and not cropping new one) */}
            {displayedImageUrl && !imageSrc && (
              <div className="space-y-2 flex flex-col items-center">
                <div className="relative w-32 h-32 rounded-full overflow-hidden border-2 border-slate-200">
                  <img
                    src={displayedImageUrl}
                    alt="Current profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Upload Area or Cropper */}
            {!imageSrc ? (
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  isDragging
                    ? 'border-[#4669ed] bg-[#4669ed]/10 scale-[1.02]'
                    : 'border-slate-300 hover:border-[#4669ed]/50 hover:bg-[#4669ed]/5'
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className={`h-12 w-12 mx-auto mb-4 transition-colors ${
                  isDragging ? 'text-[#4669ed]' : 'text-slate-400'
                }`} />
                <p className={`text-sm mb-4 transition-colors ${
                  isDragging ? 'text-[#4669ed] font-medium' : 'text-slate-600'
                }`}>
                  {isDragging ? 'Drop your image here' : 'Drag and drop an image or click below to select'}
                </p>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleUploadClick}
                  disabled={isUploading || isDeleting || isCropping}
                  className="rounded-[48px] border-2 border-slate-200"
                >
                  {(isUploading || isCropping) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isCropping ? 'Processing...' : 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <Camera className="h-4 w-4 mr-2" />
                      Choose Image
                    </>
                  )}
                </Button>
                <p className="mt-2 text-xs text-slate-500">
                  JPEG/PNG/WEBP, max 5MB, square (1:1) image recommended.
                </p>
              </div>
            ) : (
              <div className="space-y-4 relative">
                <div className="relative w-full h-[400px] bg-slate-900 rounded-xl overflow-hidden">
                  {(isCropping || isUploading) && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-xl pointer-events-none">
                      <div className="bg-white rounded-lg p-4 flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-[#4669ed]" />
                        <p className="text-sm font-medium text-slate-700">
                          {isCropping ? 'Processing image...' : 'Uploading image...'}
                        </p>
                      </div>
                    </div>
                  )}
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    minZoom={0.5}
                    maxZoom={3}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    objectFit="contain"
                    restrictPosition={false}
                  />
                </div>
                
                {/* Zoom Control */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">
                      Zoom: {Math.round(zoom * 100)}%
                    </label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
                        disabled={isCropping || isUploading || isDeleting || zoom <= 0.5}
                        className="h-8 w-8 p-0 rounded-lg border-2 border-slate-200 disabled:opacity-50"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
                        disabled={isCropping || isUploading || isDeleting || zoom >= 3}
                        className="h-8 w-8 p-0 rounded-lg border-2 border-slate-200 disabled:opacity-50"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <input
                    type="range"
                    value={zoom}
                    min={0.5}
                    max={3}
                    step={0.1}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    disabled={isCropping || isUploading || isDeleting}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#4669ed] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Action buttons for cropping */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      if (imageSrc?.startsWith('blob:')) {
                        URL.revokeObjectURL(imageSrc);
                      }
                      setImageSrc(null);
                      setIsCropping(false);
                    }}
                    disabled={isCropping || isUploading || isDeleting}
                    className="rounded-[48px] border-2 border-slate-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUploadClick}
                    disabled={isCropping || isUploading || isDeleting}
                    className="flex-1 rounded-[48px] border-2 border-slate-200"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Change Image
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-4">
            <div>
              {displayedImageUrl && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isUploading || isDeleting || isCropping || !!imageSrc}
                  className="rounded-[48px] disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Photo
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isUploading || isDeleting || isCropping}
                className="rounded-[48px] border-2 border-slate-200"
              >
                Cancel
              </Button>
              {imageSrc && (
                <Button
                  type="button"
                  onClick={handleSaveCrop}
                  disabled={isUploading || isDeleting || isCropping || !croppedAreaPixels}
                  className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] text-white disabled:opacity-50"
                >
                  {isCropping || isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isCropping ? 'Processing...' : 'Uploading...'}
                    </>
                  ) : (
                    'Save Picture'
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={showDeleteConfirm} 
        onOpenChange={(open) => {
          // Prevent closing while deletion is in progress
          if (!open && isDeleting) {
            return;
          }
          setShowDeleteConfirm(open);
        }}
      >
        <AlertDialogContent className="rounded-[24px]">
          {isDeleting && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-[24px]">
              <div className="bg-white rounded-lg p-4 flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-[#4669ed]" />
                <p className="text-sm font-medium text-slate-700">
                  Removing profile picture...
                </p>
              </div>
            </div>
          )}
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-normal text-slate-900">
              Remove Profile Picture
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Are you sure you want to remove your profile picture? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel 
              disabled={isDeleting}
              className="rounded-[48px] border-2 border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Photo'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}